import type { Mesh, Object3D } from 'three';
import { ARKIT_SHAPES } from './arkit';

/**
 * Encuentra en la escena del `.glb` las mallas que el adapter anima y los
 * huesos que rota (`docs/06_ESTANDAR_RIG.md` §3 y §7).
 *
 * **Las mallas se reconocen por sus blend shapes, no por su nombre**, con
 * la misma regla que `scripts/verify-avatar.mjs`: la malla facial es la
 * que declara más shapes y tiene que traer el catálogo ARKit; las demás
 * mallas con shapes (pestañas, dientes, cejas) son secundarias y reciben
 * el mismo peso por nombre. Así un asset que pasa `verify-avatar` se
 * puede montar aunque sus mallas se llamen distinto que las del avatar de
 * desarrollo.
 *
 * Los huesos sí se buscan por nombre: `Head`, `LeftEye` y `RightEye` son
 * requisito del estándar (06 §7, check 3).
 */

/** Huesos requeridos por el estándar (06 §3). */
export const HUESOS = { cabeza: 'Head', ojoIzq: 'LeftEye', ojoDer: 'RightEye' } as const;

/**
 * ARKit que el adapter no escribe y que por lo tanto no está en
 * `ARKIT_SHAPES`. No es un nombre ajeno: es catálogo opcional (06 §2.1).
 */
const ARKIT_OPCIONALES = ['tongueOut'];

/**
 * Por qué un `.glb` no se pudo montar, en piezas.
 *
 * Existe para que `/dev/avatar` pueda mostrar el mismo informe que
 * imprime `pnpm verify-avatar` en vez de una sola línea de texto: esa
 * página es donde un asset no conforme es el caso esperado, y quien la
 * usa necesita saber **qué** falta para pedírselo a quien produjo el
 * avatar. El producto no lo lee: ahí cualquier fallo cae al marcador.
 */
export interface DiagnosticoAsset {
  /** La malla con más shapes. `null` si ninguna declara blend shapes. */
  facial: { nombre: string; shapes: number } | null;
  /** Shapes ARKit obligatorios que la malla facial no declara. */
  faltan: string[];
  /** Shapes de la facial fuera del catálogo. **Informativo**: no invalidan. */
  ajenos: string[];
  /** Huesos requeridos que no aparecen en la escena. */
  huesosAusentes: string[];
}

const DIAGNOSTICO_VACIO: DiagnosticoAsset = {
  facial: null,
  faltan: [],
  ajenos: [],
  huesosAusentes: [],
};

/** El `.glb` cargó pero no cumple el estándar de rig. */
export class AssetInvalidoError extends Error {
  readonly diagnostico: DiagnosticoAsset;

  constructor(detalle: string, diagnostico: Partial<DiagnosticoAsset> = {}) {
    super(`El avatar no cumple docs/06_ESTANDAR_RIG.md: ${detalle}`);
    this.name = 'AssetInvalidoError';
    this.diagnostico = { ...DIAGNOSTICO_VACIO, ...diagnostico };
  }
}

function cantidadDeShapes(mesh: Mesh): number {
  return Object.keys(mesh.morphTargetDictionary ?? {}).length;
}

/**
 * Mallas con blend shapes, la facial primero.
 *
 * Una malla glTF con varias primitivas llega a three.js como varios
 * `Mesh` con el mismo diccionario: todos entran, y la facial es uno de
 * ellos (el primero en el recorrido, a igual cantidad de shapes).
 *
 * **Sobrar no invalida** (06 §2.1): un shape fuera del catálogo no tiene
 * índice en `ARKIT_SHAPES`, así que el adapter nunca le escribe peso. Se
 * registra en el diagnóstico como informativo, nada más.
 *
 * @throws AssetInvalidoError si ninguna malla tiene shapes o si a la
 *   facial le falta alguno de los que el adapter escribe.
 */
export function detectarMallas(raiz: Object3D): Mesh[] {
  const conShapes: Mesh[] = [];
  raiz.traverse((obj) => {
    const mesh = obj as Mesh;
    if (mesh.isMesh && cantidadDeShapes(mesh) > 0) conShapes.push(mesh);
  });
  if (conShapes.length === 0) {
    throw new AssetInvalidoError('ninguna malla expone blend shapes con nombre');
  }

  const facial = conShapes.reduce((mejor, m) =>
    cantidadDeShapes(m) > cantidadDeShapes(mejor) ? m : mejor,
  );
  const dict = facial.morphTargetDictionary ?? {};
  const nombres = Object.keys(dict);
  const faltan = ARKIT_SHAPES.filter((s) => dict[s] === undefined);
  const ajenos = nombres.filter(
    (n) =>
      !ARKIT_SHAPES.includes(n as (typeof ARKIT_SHAPES)[number]) && !ARKIT_OPCIONALES.includes(n),
  );
  if (faltan.length > 0) {
    throw new AssetInvalidoError(
      `a la malla facial "${facial.name}" le faltan ${faltan.length} shapes ARKit: ${faltan.join(', ')}`,
      { facial: { nombre: facial.name, shapes: nombres.length }, faltan, ajenos },
    );
  }
  return [facial, ...conShapes.filter((m) => m !== facial)];
}

/** @throws AssetInvalidoError si el hueso no existe. */
export function requerirHueso(raiz: Object3D, nombre: string): Object3D {
  const hueso = raiz.getObjectByName(nombre);
  if (!hueso)
    throw new AssetInvalidoError(`falta el hueso "${nombre}"`, { huesosAusentes: [nombre] });
  return hueso;
}
